import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RoutesPaths } from 'routes/Routes.types';
import PageWrapper from 'components/Base/PageWrapper';
import { useGetOrganizationByIdQuery } from 'redux/apiSlice/organizations.slice';
import { Tabs } from 'components/Base/Tabs';
import { TabsProps } from 'components/Base/Tabs/Tabs.types';
import { useTranslation } from '@packages/utils';
import { TRtkErrorType } from 'data/types/generic.types';
import { TBackButtonState } from 'components/Base/PageWrapper/PageWrapper.types';
import useKeepTabsInUrl from 'hooks/useKeepTabsInUrl';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import { useCheckPermission } from 'hooks/useCheckPermission';
import Notes from 'components/Shared/Notes';
import { ENoteEntityType } from 'data/types/notes.types';
import OutputTab from 'components/Shared/OutputTab';
import { organizationDetailsKey } from './OrganizationDetails.data';
import { EOrganizationDetailsTabsInUrl } from './OrganizationDetails.types';
import DetailsTab from './DetailsTab';
import { organizationListKey } from '../OrganizationList/OrganizationList.data';
import Actions from './Actions';
import OrganizationKitsInventoryManagementTab from './OrganizationKitsInventoryManagementTab/OrganizationKitsInventoryManagementTab';

const OrganizationDetails = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { t: tOrgList } = useTranslation({
    keyPrefix: organizationListKey,
  });
  const { t } = useTranslation({
    keyPrefix: organizationDetailsKey,
  });

  const goBackState = (location.state as TBackButtonState) || {
    backUrl: `/app/${RoutesPaths.ORGANIZATION}`,
    backButtonSuffix: tOrgList('TITLE'),
  };

  const { id } = params as { id: string };
  const { data, isLoading, error } = useGetOrganizationByIdQuery({
    id,
  });

  const isOrgKitsEnabled = useIsFeatureEnabled('org_kits');
  const canEdit = useCheckPermission('ORGANIZATION_EDIT');

  const onBackClick = () => {
    navigate(goBackState.backUrl);
  };

  const isOutputsRoute = location.pathname.endsWith('/inputs-outputs');

  const getDefaultTab = () => {
    if (!canEdit)
      return EOrganizationDetailsTabsInUrl.ORGANIZATION_KITS_INVENTORY;
    if (isOutputsRoute) return EOrganizationDetailsTabsInUrl.OUTPUT;
    return EOrganizationDetailsTabsInUrl.DETAILS;
  };

  const { tabInUrl } = useKeepTabsInUrl({
    defaultTab: getDefaultTab(),
  });

  const handleTabChange = (key: string) => {
    if (!id) return;
    if (key === EOrganizationDetailsTabsInUrl.OUTPUT) {
      navigate(
        `/app/${RoutesPaths.ORGANIZATION_DETAILS}/inputs-outputs`.replace(
          ':id',
          id,
        ),
      );
    } else {
      const basePath = `/app/${RoutesPaths.ORGANIZATION_DETAILS}`.replace(
        ':id',
        id,
      );
      navigate(`${basePath}?tab=${key}`);
    }
  };

  const tabItems: TabsProps['items'] = [
    {
      key: EOrganizationDetailsTabsInUrl.DETAILS,
      label: <span data-cy="details_tab">{t('tabs-titles.DETAILS')}</span>,
      children: <DetailsTab detailsData={data} />,
      show: canEdit,
    },
    {
      key: EOrganizationDetailsTabsInUrl.OUTPUT,
      label: (
        <span data-cy="output_tab">{t('tabs-titles.OUTPUT', 'Outputs')}</span>
      ),
      children: (
        <OutputTab
          id={id}
          sourceType="ORGANIZATION"
          isWorkshopOrEvent={false}
        />
      ),
      show: canEdit,
    },
    {
      key: EOrganizationDetailsTabsInUrl.NOTES,
      label: <span data-cy="notes_tab">{t('tabs-titles.NOTES', 'Notes')}</span>,
      children: (
        <Notes entityType={ENoteEntityType.ORGANIZATION} entityId={id} />
      ),
      show: canEdit,
    },
    {
      key: EOrganizationDetailsTabsInUrl.ORGANIZATION_KITS_INVENTORY,
      label: <span data-cy="kit_inventory_tab">Kit Inventory</span>,
      children: <OrganizationKitsInventoryManagementTab detailsData={data} />,
      show: isOrgKitsEnabled,
    },
  ];

  return (
    <PageWrapper
      title={data?.name.value}
      backButton={{
        labelPrefix: data?.name.value || '',
        tooltip: goBackState.backButtonSuffix,
        onClick: onBackClick,
      }}
      isLoading={isLoading}
      error={data ? undefined : (error as TRtkErrorType)}
      actions={canEdit ? <Actions id={id} data={data} /> : undefined}
    >
      <Tabs
        defaultActiveKey={tabInUrl}
        onChange={handleTabChange}
        items={tabItems}
      />
    </PageWrapper>
  );
};

export default OrganizationDetails;
